import { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material'
import { ClientSummary, Deposit, FeeClass, Fund, StrategyKey } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { parseAmount, validateAmount } from '../../validation'

interface ManualDepositDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (deposit: Deposit) => void
}

function optionLabel(client: ClientSummary): string {
  const id = client.personalNumber ?? client.companyId
  return id ? `${client.fullName} (${id})` : client.fullName
}

/**
 * Ruční vklad stávajícího klienta mimo generátor (F3-D): peníze už přišly, vklad
 * vzniká rovnou jako ZAPLACENO a přeskočí frontu plateb. Jen backoffice.
 */
export function ManualDepositDialog({ open, onClose, onCreated }: ManualDepositDialogProps) {
  const api = useOpsApi()

  const [selectedClient, setSelectedClient] = useState<ClientSummary | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [options, setOptions] = useState<ClientSummary[]>([])
  const [searching, setSearching] = useState(false)

  const [funds, setFunds] = useState<Fund[]>([])
  const [strategy, setStrategy] = useState<StrategyKey | ''>('')
  // Třída fondu určuje sazbu MF i SF navždy — u ručního vkladu ji zadává obsluha
  // (u generovaného vkladu plyne z varianty smlouvy). Default B (NORM).
  const [feeClass, setFeeClass] = useState<FeeClass>('B')
  const [amountText, setAmountText] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [note, setNote] = useState('')

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Vyčistit formulář při každém otevření.
  useEffect(() => {
    if (open) {
      setSelectedClient(null)
      setSearchInput('')
      setOptions([])
      setStrategy('')
      setFeeClass('B')
      setAmountText('')
      setAccountNumber('')
      setNote('')
      setErrors({})
      setSubmitError(null)
      setSubmitting(false)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    api
      .listFunds()
      .then((loaded) => {
        if (!cancelled) setFunds(loaded)
      })
      .catch(() => {
        if (!cancelled) setFunds([])
      })
    return () => {
      cancelled = true
    }
  }, [open, api])

  // Debounced hledání klienta (vzor ClientSection).
  useEffect(() => {
    if (!open) return
    // Prázdný/krátký dotaz vrací z BE VŠECHNY klienty teritoria — nehledáme.
    if (searchInput.trim().length < 2) {
      setOptions([])
      setSearching(false)
      return
    }
    let cancelled = false
    setSearching(true)
    const timer = setTimeout(() => {
      api
        .searchClients(searchInput)
        .then((found) => {
          if (!cancelled) setOptions(found)
        })
        .catch(() => {
          if (!cancelled) setOptions([])
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, api, searchInput])

  const selectedFund = funds.find((f) => f.key === strategy)
  // Fond s jedinou třídou (TREASURY) — A je jediná možnost.
  const singleClass = Boolean(selectedFund?.singleClass)
  const effectiveFeeClass: FeeClass = singleClass ? 'A' : feeClass

  const validate = (): Record<string, string> => {
    const found: Record<string, string> = {}
    if (!selectedClient) found.client = 'Vyberte klienta'
    if (!strategy) found.strategy = 'Vyberte strategii'
    const amountError = validateAmount(amountText)
    if (amountError) found.amount = amountError
    return found
  }

  const handleSubmit = async () => {
    const found = validate()
    setErrors(found)
    setSubmitError(null)
    if (Object.keys(found).length > 0) return

    setSubmitting(true)
    try {
      const deposit = await api.createManualDeposit({
        clientId: selectedClient!.id,
        strategy: strategy as StrategyKey,
        feeClass: effectiveFeeClass,
        amount: parseAmount(amountText) ?? 0,
        accountNumber: accountNumber.trim() || undefined,
        note: note.trim() || undefined,
      })
      onCreated(deposit)
    } catch (err) {
      setSubmitError(apiError(err, 'Vytvoření vkladu selhalo'))
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Ruční vklad</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <Alert severity="info">
            Vklad vznikne rovnou jako ZAPLACENO — přeskočí frontu plateb (peníze už přišly).
          </Alert>

          <Autocomplete
            size="small"
            options={options}
            value={selectedClient}
            onChange={(_event, value) => setSelectedClient(value)}
            inputValue={searchInput}
            onInputChange={(_event, value) => setSearchInput(value)}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={searching}
            disabled={submitting}
            noOptionsText="Žádný klient nenalezen"
            loadingText="Hledám…"
            // Filtrování dělá mock/BE — FE nefiltruje podruhé.
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Klient (jméno, RČ, IČO) *"
                error={Boolean(errors.client)}
                helperText={errors.client}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            select
            size="small"
            label="Strategie *"
            value={strategy}
            onChange={(event) => setStrategy(event.target.value as StrategyKey)}
            error={Boolean(errors.strategy)}
            helperText={errors.strategy}
            disabled={submitting}
          >
            {funds.map((fund) => (
              <MenuItem key={fund.key} value={fund.key}>
                {fund.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Třída (poplatky) *"
            value={effectiveFeeClass}
            onChange={(event) => setFeeClass(event.target.value as FeeClass)}
            helperText={
              singleClass
                ? 'Fond má jedinou třídu — vždy A.'
                : 'A = EB (nižší MF i SF), B = NORM/Dohoda (vyšší). Určuje sazby klienta natrvalo.'
            }
            disabled={submitting || singleClass}
          >
            <MenuItem value="A">A (EB)</MenuItem>
            <MenuItem value="B">B (NORM)</MenuItem>
          </TextField>

          <TextField
            size="small"
            label="Částka (Kč) *"
            value={amountText}
            onChange={(event) => setAmountText(event.target.value)}
            error={Boolean(errors.amount)}
            helperText={errors.amount}
            placeholder="1 000 000"
            disabled={submitting}
          />

          <TextField
            size="small"
            label="Číslo účtu klienta"
            value={accountNumber}
            onChange={(event) => setAccountNumber(event.target.value)}
            placeholder="123456789/0300"
            disabled={submitting}
          />

          <TextField
            size="small"
            label="Poznámka"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            multiline
            minRows={2}
            disabled={submitting}
          />

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" color="inherit" onClick={onClose} disabled={submitting}>
          Zrušit
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          Vytvořit vklad
        </Button>
      </DialogActions>
    </Dialog>
  )
}
